import { Injectable, NgZone } from '@angular/core';
import { CellPosition, NAVIGATION_KEYS, KEYBOARD_SHORTCUTS } from '../models/child-table.models';

/**
 * Child Table Accessibility Service
 * Handles screen reader announcements, focus management, and keyboard navigation
 * Framework-agnostic patterns that can be reused in React
 *
 * WCAG 2.1 AA compliant:
 * - 2.1.1 Keyboard: all functionality keyboard-accessible
 * - 2.4.7 Focus Visible: clear focus indicators
 * - 4.1.2 Name, Role, Value: correct ARIA attributes
 * - Non-redundant announcements: throttled, concise
 */
@Injectable()
export class ChildTableA11yService {
  /** Current announcement bindings (template reads these directly) */
  currentAnnouncement = '';
  currentAssertiveAnnouncement = '';

  /** Debounce timer (200ms, matching AG Grid) */
  private announceTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly ANNOUNCE_DEBOUNCE_MS = 200;

  /** OS detection for keyboard shortcuts */
  private isMac = /Mac|iPhone|iPod|iPad/i.test(navigator.platform || navigator.userAgent);
  private get modKey(): string {
    return this.isMac ? '⌘' : 'Ctrl';
  }

  constructor(private ngZone: NgZone) {}

  /**
   * Announce a message to screen readers via live region.
   * Uses clear-then-set pattern (AG Grid technique): clears the text first,
   * then sets new text on the next tick, forcing VoiceOver to re-read.
   * Polite announcements are debounced at 200ms; assertive are immediate.
   */
  announce(message: string, priority: 'polite' | 'assertive' = 'polite'): void {
    if (this.announceTimer) clearTimeout(this.announceTimer);

    if (priority === 'assertive') {
      this.ngZone.run(() => {
        this.currentAssertiveAnnouncement = '';
        setTimeout(() => {
          this.currentAssertiveAnnouncement = message;
          setTimeout(() => { this.currentAssertiveAnnouncement = ''; }, 1000);
        });
      });
      return;
    }

    this.announceTimer = setTimeout(() => {
      this.ngZone.run(() => {
        this.currentAnnouncement = '';
        setTimeout(() => {
          this.currentAnnouncement = message;
          setTimeout(() => { this.currentAnnouncement = ''; }, 1000);
        });
      });
      this.announceTimer = null;
    }, this.ANNOUNCE_DEBOUNCE_MS);
  }

  /**
   * Get the next cell position based on key press
   *
   * Column layout (arrow-key order):
   *   colIndex -1  = row-select cell (number/checkbox/drag)
   *   colIndex -2  = actions cell (kebab menu)
   *   colIndex 0..N = data columns
   *
   * Arrow keys navigate all columns including utility columns.
   * Tab only navigates data columns (0..N) -- at boundaries, returns null to let focus exit the grid.
   */
  getNextCellPosition(
    currentPosition: CellPosition,
    key: string,
    totalRows: number,
    totalDataColumns: number,
    shiftKey: boolean = false,
    hasRowSelectCol: boolean = true,
    hasActionsCol: boolean = false,
    ctrlKey: boolean = false
  ): CellPosition | null {
    const { rowIndex, columnIndex } = currentPosition;

    const firstColIndex = hasRowSelectCol ? -1 : (hasActionsCol ? -2 : 0);
    const lastColIndex = totalDataColumns - 1;

    const getNextRight = (col: number): number => {
      if (col === -1 && hasActionsCol) return -2;
      if (col === -2) return 0;
      return col + 1;
    };

    const getNextLeft = (col: number): number => {
      if (col === 0 && hasActionsCol) return -2;
      if (col === -2 && hasRowSelectCol) return -1;
      if (col === -2) return -2;
      return col - 1;
    };

    switch (key) {
      case NAVIGATION_KEYS.ARROW_UP:
        if (rowIndex > 0) {
          return { rowIndex: rowIndex - 1, columnIndex };
        } else if (rowIndex === 0) {
          return { rowIndex: -1, columnIndex: columnIndex === -2 ? -2 : columnIndex };
        }
        break;

      case NAVIGATION_KEYS.ARROW_DOWN:
        if (rowIndex === -1) {
          return { rowIndex: 0, columnIndex: columnIndex === -2 ? -2 : columnIndex };
        } else if (rowIndex < totalRows - 1) {
          return { rowIndex: rowIndex + 1, columnIndex };
        }
        break;

      case NAVIGATION_KEYS.ARROW_LEFT: {
        const next = getNextLeft(columnIndex);
        if (next !== columnIndex) {
          return { rowIndex, columnIndex: next };
        }
        break;
      }

      case NAVIGATION_KEYS.ARROW_RIGHT: {
        const next = getNextRight(columnIndex);
        if (next <= lastColIndex) {
          return { rowIndex, columnIndex: next };
        }
        break;
      }

      case NAVIGATION_KEYS.HOME:
        // Ctrl+Home: first cell in grid (WAI-ARIA data grid pattern)
        if (ctrlKey) {
          return { rowIndex: 0, columnIndex: 0 };
        }
        return { rowIndex, columnIndex: firstColIndex };

      case NAVIGATION_KEYS.END:
        // Ctrl+End: last cell in grid (WAI-ARIA data grid pattern)
        if (ctrlKey) {
          return { rowIndex: Math.max(0, totalRows - 1), columnIndex: lastColIndex };
        }
        return { rowIndex, columnIndex: lastColIndex };

      case NAVIGATION_KEYS.PAGE_UP:
        return { rowIndex: Math.max(0, rowIndex - 10), columnIndex };

      case NAVIGATION_KEYS.PAGE_DOWN:
        return { rowIndex: Math.min(totalRows - 1, rowIndex + 10), columnIndex };
    }

    return null;
  }

  /**
   * Check if a keyboard event matches a shortcut
   * Works correctly on both Mac (metaKey) and Windows/Linux (ctrlKey)
   */
  matchesShortcut(
    event: KeyboardEvent,
    shortcut: { key: string; ctrlKey?: boolean; shiftKey?: boolean; altKey?: boolean }
  ): boolean {
    const ctrlOrMeta = this.isMac ? event.metaKey : event.ctrlKey;
    return (
      event.key.toLowerCase() === shortcut.key.toLowerCase() &&
      !!ctrlOrMeta === !!shortcut.ctrlKey &&
      !!event.shiftKey === !!shortcut.shiftKey &&
      !!event.altKey === !!shortcut.altKey
    );
  }

  /** Check if key is a navigation key */
  isNavigationKey(key: string): boolean {
    return Object.values(NAVIGATION_KEYS).includes(key as any);
  }

  /** Check if key should start editing */
  isEditTriggerKey(key: string): boolean {
    return key === NAVIGATION_KEYS.ENTER || key === NAVIGATION_KEYS.F2;
  }

  /** Check if key should cancel editing */
  isCancelKey(key: string): boolean {
    return key === NAVIGATION_KEYS.ESCAPE;
  }

  isMoveRowUpShortcut(event: KeyboardEvent): boolean {
    return this.matchesShortcut(event, KEYBOARD_SHORTCUTS.MOVE_ROW_UP);
  }

  isMoveRowDownShortcut(event: KeyboardEvent): boolean {
    return this.matchesShortcut(event, KEYBOARD_SHORTCUTS.MOVE_ROW_DOWN);
  }

  isSelectAllShortcut(event: KeyboardEvent): boolean {
    return this.matchesShortcut(event, KEYBOARD_SHORTCUTS.SELECT_ALL);
  }

  isAddRowShortcut(event: KeyboardEvent): boolean {
    return this.matchesShortcut(event, KEYBOARD_SHORTCUTS.ADD_ROW);
  }

  isDeleteRowShortcut(event: KeyboardEvent): boolean {
    return this.matchesShortcut(event, KEYBOARD_SHORTCUTS.DELETE_ROW);
  }

  isCopyShortcut(event: KeyboardEvent): boolean {
    return this.matchesShortcut(event, KEYBOARD_SHORTCUTS.COPY);
  }

  isCutShortcut(event: KeyboardEvent): boolean {
    return this.matchesShortcut(event, KEYBOARD_SHORTCUTS.CUT);
  }

  isPasteShortcut(event: KeyboardEvent): boolean {
    return this.matchesShortcut(event, KEYBOARD_SHORTCUTS.PASTE);
  }

  isUndoShortcut(event: KeyboardEvent): boolean {
    return this.matchesShortcut(event, KEYBOARD_SHORTCUTS.UNDO);
  }

  /** Focus a cell element by position */
  focusCell(tableElement: HTMLElement, position: CellPosition): HTMLElement | null {
    const cell = tableElement.querySelector(
      `[data-row-index="${position.rowIndex}"][data-col-index="${position.columnIndex}"]`
    ) as HTMLElement;

    if (cell) {
      cell.focus();
      return cell;
    }
    return null;
  }

  /**
   * Traps focus within a given container element.
   * Useful for modals and complex editors.
   */
  trapFocus(event: KeyboardEvent, container: HTMLElement): void {
    const focusable = container.querySelectorAll<HTMLElement>(
      'button:not([disabled]):not([tabindex="-1"]), input:not([disabled]):not([tabindex="-1"]), select:not([disabled]):not([tabindex="-1"]), textarea:not([disabled]):not([tabindex="-1"]), [tabindex]:not([tabindex="-1"])'
    );
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  /** Get cell position from element */
  getCellPositionFromElement(element: HTMLElement): CellPosition | null {
    const cell = element.closest('[data-row-index][data-col-index]') as HTMLElement;
    if (!cell) return null;

    const rowIndex = parseInt(cell.dataset['rowIndex'] || '-1', 10);
    const columnIndex = parseInt(cell.dataset['colIndex'] || '-1', 10);

    if (rowIndex < 0 || columnIndex < 0) return null;
    return { rowIndex, columnIndex };
  }

  // --- State change announcements (live region ONLY for actions/state, never cell content) ---

  announceSort(columnLabel: string, direction: 'asc' | 'desc' | null): void {
    if (!direction) {
      this.announce('Sort cleared');
    } else {
      this.announce(`Sorted by ${columnLabel} ${direction === 'asc' ? 'ascending' : 'descending'}`);
    }
  }

  announceSelection(count: number): void {
    this.announce(`${count} selected`);
  }

  announceRowAdded(rowNumber: number): void {
    this.announce(`Row ${rowNumber} added`);
  }

  announceRowsDeleted(count: number): void {
    this.announce(`${count} row${count === 1 ? '' : 's'} deleted`);
  }

  announceRowMoved(fromPosition: number, toPosition: number): void {
    this.announce(`Moved to row ${toPosition}`);
  }

  announceEditStarted(columnLabel: string, editorType?: string, currentValue?: any): void {
    const parts = [`Editing ${columnLabel}`];
    if (currentValue !== undefined && currentValue !== null && currentValue !== '') {
      parts.push(String(currentValue));
    }
    if (editorType) {
      parts.push(editorType);
    }
    this.announce(parts.join(', '));
  }

  announceEditSaved(): void {
    this.announce('Saved');
  }

  announceEditCancelled(): void {
    this.announce('Cancelled');
  }

  announceValidationError(error: string): void {
    this.announce(error, 'assertive');
  }

  announceFilterResults(count: number): void {
    this.announce(`${count} record${count === 1 ? '' : 's'}`);
  }

  announceHeaderInteractionMode(columnLabel: string): void {
    this.announce(`${columnLabel} column actions`);
  }

  announceHeaderInteractionExit(): void {
    this.announce('Exited column actions');
  }

  /** Get keyboard shortcuts help text */
  getKeyboardShortcutsHelp(): string[] {
    const mod = this.modKey;
    return [
      'Tab: Move between header and data areas',
      'Arrow keys: Navigate cells',
      'Enter / F2: Edit cell',
      'Escape: Cancel edit',
      'Space: Toggle selection',
      'Space on header: Access column actions (sort, menu, resize)',
      'Enter on header: Sort column',
      'Shift+F10 or Ctrl+M: Row actions menu',
      `${mod}+C: Copy`,
      `${mod}+X: Cut`,
      `${mod}+V: Paste`,
      `${mod}+Z: Undo paste error`,
      `${mod}+Shift+Up/Down: Move row`,
      `${mod}+A: Select all`,
      'Insert: Add row',
      `${mod}+Delete: Delete selected`,
      'Home / End: First / last cell',
      'Page Up/Down: Jump 10 rows',
    ];
  }
}
